import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import Table from 'further-ui/components/Table';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { uniqBy } from 'lodash';
import {
  CapitalActionFormState,
  UpdateCapitalActionFormValues,
} from '../../../hooks/useCapitalActionForm';
import DistributableActionsHistory from './DistributableActionsHistory';
import PageSection from 'components/new/PageSection';

type Props = {
  index: number;
  baseCurrency: CurrencyCode;
  onAmountChange: (
    commitmentId: string,
    amount: number,
    field: 'outsideOfCarry' | 'recallableOutsideOfCarry',
  ) => void;
  fundsRemainingToDistribute: Array<{
    commitmentId: string;
    investorLabel: string;
    outsideOfCarryCalculation: {
      fundsFromIncomes: number;
      fundsFromFxGains: number;
      distributedToDate: number;
      actionHistory: Array<{
        actionRef: string;
        actionId: string;
        amountLcy?: number | null;
        date: CalendarDay;
      }>;
    };
    availableToDistributeOutsideOfCarry: number;
  }>;
};

const OutsideOfCarryDistribution: React.FC<Props> = ({
  fundsRemainingToDistribute,
  baseCurrency,
  index,
  onAmountChange,
}) => {
  const {
    formMethods: { watch, formState },
  } = useFormWithStateContext<
    UpdateCapitalActionFormValues,
    CapitalActionFormState
  >();

  const distributionValues = Object.fromEntries(
    (watch(`entries.${index}.capitalBreakdown`) ?? []).map((row) => [
      row.commitmentId,
      {
        total: row.fundsDistributed?.outsideOfCarry ?? 0,
        recallable: row.fundsDistributed?.recallableOutsideOfCarry ?? 0,
      },
    ]),
  );

  const actionHistory = uniqBy(
    fundsRemainingToDistribute.flatMap(
      (details) => details.outsideOfCarryCalculation.actionHistory,
    ),
    'actionId',
  );

  const columns = [
    {
      key: 'investorName',
      label: 'Investor',
      width: 200,
    },
    {
      key: 'distributedToDate',
      label: 'Total distributions',
      width: 180,
    },
    {
      key: 'undistributed',
      label: 'Undistributed',
      width: 180,
    },
    {
      key: 'distribution',
      label: 'Distribution',
      width: 180,
      render: (row: { commitmentId: string }) => (
        <NumberInput
          value={distributionValues[row.commitmentId]?.total ?? 0}
          onChange={(value) => {
            onAmountChange(row.commitmentId, value ?? 0, 'outsideOfCarry');
          }}
          decimals={2}
          placeholder="Enter amount"
          currency={baseCurrency}
          clearZeroValueOnFocus
        />
      ),
    },
    {
      key: 'recallableDistribution',
      label: 'Of which recallable',
      withVerticalBorder: true,
      width: 180,
      render: (row: { commitmentId: string }, rowIndex: number) => (
        <NumberInput
          value={distributionValues[row.commitmentId]?.recallable ?? 0}
          onChange={(value) => {
            onAmountChange(
              row.commitmentId,
              value ?? 0,
              'recallableOutsideOfCarry',
            );
          }}
          decimals={2}
          placeholder="Enter amount"
          currency={baseCurrency}
          clearZeroValueOnFocus
          error={
            !!formState.errors.entries?.[index]?.capitalBreakdown?.[rowIndex]
              ?.fundsDistributed?.recallableOutsideOfCarry
          }
        />
      ),
    },
    {
      key: 'fundsFromIncomes',
      label: 'Income',
      width: 180,
    },
    {
      key: 'fundsFromFxGains',
      label: 'Realised FX gains on incomes/expenses',
      width: 180,
    },
  ];

  const rows = fundsRemainingToDistribute.map((details) => ({
    id: details.commitmentId,
    commitmentId: details.commitmentId,
    investorName: details.investorLabel,
    fundsFromFxGains: numberToCurrencyString(
      details.outsideOfCarryCalculation.fundsFromFxGains,
      {
        currency: baseCurrency,
        negativeInBrackets: true,
      },
    ),
    fundsFromIncomes: numberToCurrencyString(
      details.outsideOfCarryCalculation.fundsFromIncomes,
      {
        currency: baseCurrency,
        negativeInBrackets: true,
      },
    ),
    distributedToDate: numberToCurrencyString(
      details.outsideOfCarryCalculation.distributedToDate,
      {
        currency: baseCurrency,
        negativeInBrackets: true,
      },
    ),
    undistributed: numberToCurrencyString(
      details.availableToDistributeOutsideOfCarry,
      {
        currency: baseCurrency,
        negativeInBrackets: true,
      },
    ),
  }));

  return (
    <PageSection
      withoutBottomDivider
      title="Process distribution (not subject to carried interest)"
      spacing="sm"
    >
      <Column spacing="lg">
        {actionHistory.length > 0 && (
          <DistributableActionsHistory
            baseCurrency={baseCurrency}
            actionHistory={actionHistory}
          />
        )}
        <Table scrollable columns={columns} rows={rows} />
      </Column>
    </PageSection>
  );
};

export default OutsideOfCarryDistribution;
